/**
 * NavigationOverlay Component
 * Hamburger menu overlay for full-screen modes
 */

import { t } from '../i18n/config.js';

export class NavigationOverlay {
    constructor(router, profileData) {
        this.router = router;
        this.profileData = profileData;
        this.element = null;
        this.hamburgerButton = null;
        this.isOpen = false;
    }

    /**
     * Render hamburger button
     */
    renderHamburgerButton(container) {
        const button = document.createElement('button');
        button.className = 'hamburger-button';
        button.setAttribute('aria-label', t('child.navigation.openMenu') || 'Open menu');
        button.setAttribute('aria-expanded', 'false');

        button.innerHTML = `
      <svg class="hamburger-icon" viewBox="0 0 24 24" width="24" height="24">
        <line x1="4" y1="8" x2="20" y2="8" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
        <line x1="4" y1="16" x2="20" y2="16" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
        <line x1="4" y1="24" x2="20" y2="24" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
      </svg>
    `;

        button.addEventListener('click', () => this.open());

        container.appendChild(button);
        this.hamburgerButton = button;

        // Make visible
        button.classList.add('visible');
    }

    /**
     * Render overlay
     */
    renderOverlay(container) {
        const overlay = document.createElement('div');
        overlay.className = 'navigation-overlay';
        overlay.setAttribute('role', 'dialog');
        overlay.setAttribute('aria-modal', 'true');
        overlay.setAttribute('aria-label', t('child.navigation.menu') || 'Navigation menu');

        overlay.innerHTML = `
      <div class="overlay-backdrop" aria-hidden="true"></div>
      <div class="overlay-panel">
        <button class="overlay-close" aria-label="${t('child.navigation.closeMenu') || 'Close menu'}">
          <svg viewBox="0 0 24 24" width="24" height="24">
            <line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
            <line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
          </svg>
        </button>
        <nav class="overlay-nav">
          <button class="overlay-nav-item" data-route="profile">
            <svg class="overlay-icon" viewBox="0 0 24 24" width="40" height="40" fill="currentColor">
              <circle cx="12" cy="8" r="4" />
              <path d="M4 20c0-4 4-6 8-6s8 2 8 6" stroke="currentColor" stroke-width="2" fill="none" />
            </svg>
            <span class="overlay-label">${t('child.navigation.profile') || 'Profile'}</span>
          </button>
          <button class="overlay-nav-item" data-route="library">
            <svg class="overlay-icon" viewBox="0 0 24 24" width="40" height="40" fill="currentColor">
              <rect x="4" y="6" width="16" height="14" rx="2" stroke="currentColor" stroke-width="2" fill="none" />
              <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" stroke="currentColor" stroke-width="2" fill="none" />
            </svg>
            <span class="overlay-label">${t('child.navigation.library') || 'Library'}</span>
          </button>
          <button class="overlay-nav-item" data-route="achievements">
            <svg class="overlay-icon" viewBox="0 0 24 24" width="40" height="40" fill="currentColor">
              <path d="M12 2l3 6 6 1-4.5 4.5L18 20l-6-3-6 3 1.5-6.5L3 9l6-1z" />
            </svg>
            <span class="overlay-label">${t('child.navigation.achievements') || 'Achievements'}</span>
          </button>
        </nav>
      </div>
    `;

        container.appendChild(overlay);
        this.element = overlay;

        // Bind events
        this.bindEvents();
    }

    /**
     * Bind overlay events
     */
    bindEvents() {
        if (!this.element) return;

        // Close button
        const closeBtn = this.element.querySelector('.overlay-close');
        closeBtn.addEventListener('click', () => this.close());

        // Backdrop click
        const backdrop = this.element.querySelector('.overlay-backdrop');
        backdrop.addEventListener('click', () => this.close());

        // Navigation items
        const navItems = this.element.querySelectorAll('.overlay-nav-item');
        navItems.forEach(item => {
            item.addEventListener('click', () => {
                const route = item.dataset.route;
                this.close();
                this.router.navigate(route, this.profileData);
            });
        });

        // Keyboard navigation
        document.addEventListener('keydown', this.handleKeydown.bind(this));
    }

    /**
     * Handle keyboard events
     */
    handleKeydown(e) {
        if (this.isOpen && e.key === 'Escape') {
            this.close();
            e.preventDefault();
        }
    }

    /**
     * Open overlay
     */
    open() {
        if (!this.element) return;

        this.isOpen = true;
        this.element.classList.add('open');

        if (this.hamburgerButton) {
            this.hamburgerButton.setAttribute('aria-expanded', 'true');
        }

        // Focus first nav item
        const firstNavItem = this.element.querySelector('.overlay-nav-item');
        if (firstNavItem) {
            setTimeout(() => firstNavItem.focus(), 100);
        }
    }

    /**
     * Close overlay
     */
    close() {
        if (!this.element) return;

        this.isOpen = false;
        this.element.classList.remove('open');

        if (this.hamburgerButton) {
            this.hamburgerButton.setAttribute('aria-expanded', 'false');
            this.hamburgerButton.focus();
        }
    }

    /**
     * Show hamburger button
     */
    showHamburger() {
        if (this.hamburgerButton) {
            this.hamburgerButton.classList.add('visible');
        }
    }

    /**
     * Hide hamburger button
     */
    hideHamburger() {
        if (this.hamburgerButton) {
            this.hamburgerButton.classList.remove('visible');
        }
    }

    /**
     * Destroy overlay
     */
    destroy() {
        if (this.element && this.element.parentNode) {
            this.element.parentNode.removeChild(this.element);
        }
        if (this.hamburgerButton && this.hamburgerButton.parentNode) {
            this.hamburgerButton.parentNode.removeChild(this.hamburgerButton);
        }
        this.element = null;
        this.hamburgerButton = null;
    }
}
